// tslint:disable:max-line-length

import React from "react";
import { Link } from "react-router";

import * as WebRoutes from "@travellocal/utils/webRoutes";
import { Accordion, IAccordionItem } from "../../../../../../core/atoms/atoms";
import { RoutesInvoker } from "../../../../../routesInvoker";
import { officeHours, officePhone, teamEmail } from "../../utils";

interface FAQProps {
  enquiry?: IEnquiry;
  itinerary?: IEnquiryItinerarySummary;
}

const faqItems: IAccordionItem<FAQProps>[] = [
  {
    title: "What part of my trip can be personalised?",
    content: () => (
      <p>
        The beauty of a tailor-made holiday is that everything can be personalised from start to finish - the route you take, the way you travel, whether you have guides, what activities you partake in… whatever it is, you can choose. Speak to your local travel agent and they will plan everything to your budget, tastes and requirements.
      </p>
    ),
  },
  {
    title: "How long do I need to wait for a reply?",
    content: () => (
      <>
        <p>Our local experts always aim to get back to you within 24 hours of your initial enquiry, though please be aware that they do often have weekends off. If this is the case, and you have sent your enquiry over the weekend, then they will reply to you on Monday.</p>
        <p>If your local expert has not replied to you within five working days, you can contact the TravelLocal team on {officePhone} (available {officeHours}), or send us an email at {teamEmail}.</p>
      </>
    ),
  },
  {
    title: "Do I need a visa? Can my agent arrange it for me?",
    content: ({ itinerary }) => (
      <>
        <p>It’s important to check whether you need to obtain a visa in advance. Although our local experts are always happy to give advice and answer any questions, the best place to find out whether you need a visa is directly from the embassy of the country you’re visiting.</p>
        <p>Neither TravelLocal nor your local agent are able to make any necessary visa applications on your behalf.</p>
        <p>You can always give us a call at the TravelLocal office if you need further assistance, on {officePhone} - we're available {officeHours}.</p>

        <h3>Passport expiry</h3>
        <p>Many destinations and airlines require a minimum of six months passport validity.</p>

        <h3>Advice for UK Citizens</h3>
        <p>Information about visa requirements and general travel advice for any country is <a href="https://www.gov.uk/foreign-travel-advice" target="_blank">available on the UK Government website</a>.</p>

        {itinerary && (
          <div>
            <h4>Countries in your current itinerary</h4>
            <ul>
              {(itinerary.countries || []).map(country => {
                if (!country.externalLinks) {
                  return;
                }

                const link = country.externalLinks.find(l => l.label === "visa");
                if (!link) {
                  return;
                }

                return (
                  <li><a key={country.id} href={link.url} target="_blank">Travel advice for {country.name}</a></li>
                );
              })}
            </ul>
          </div>
        )}
      </>
    ),
  },
  {
    title: "I have reduced mobility, how do I know if a trip is suitable for me?",
    content: () => (
      <>
        <p>If you or any of your travel companions have reduced mobility, or other unique travel requirements, then make sure that you let your local expert know so that they can tailor your holiday accordingly.</p>
        <p>You can always give us a call at the TravelLocal office if you need further assistance - our number is {officePhone}, and we're available to talk {officeHours}.</p>
      </>
    ),
  },
  {
    title: "What are my rights?",
    content: ({ enquiry }) => (
      <>
        <p>You have various rights when booking, including cancellation of your trip. These are outlined in the following documents:</p>

        {enquiry && (
          <ul>
            <li><Link to={WebRoutes.termsAndConditions()}>TravelLocal Terms &amp; Conditions</Link></li>
            {enquiry.isConnected && (
              <>
                <li><Link to={WebRoutes.customerEnquiryBookingTerms(enquiry.id)}>{enquiry.lto.name} Terms &amp; Conditions</Link></li>
                <li><Link to={WebRoutes.ltoPtr(enquiry.lto.slug)}>Package Travel and Linked Travel Regulations 2018 (Schedule 2)</Link></li>
              </>
            )}
          </ul>
        )}

        <p>If you have any questions, please contact the TravelLocal team on {officePhone} during office hours, or email us at {teamEmail}.</p>
      </>
    ),
  },
  {
    title: "Is my money safe with TravelLocal?",
    content: () => (
      <>
        <p>Yes, your money is 100% protected.</p>
        <p>In fact, we are the safest way you could book a holiday with a local travel company, anywhere in the world. That’s because we are members of ABTA - the Association of British Travel Agents. This industry body is known and respected worldwide and offers 100% protection of all customer money – regardless of where those customers are in the world or where they are travelling to - by way of an insurance bond. You pay TravelLocal, a UK company, online by credit or debit card, thereby benefiting from the ABTA financial protection and guarantees, and we pay the local travel company. So there’s no need for you to transfer money to a company in a far-flung country.</p>
        <p>In addition we also insure all bookings made via our website with Vantage, a UK insurance company, so you are also protected in the unlikely event of the bankruptcy of the local travel company, as well as TravelLocal itself. We undertake an extensive due diligence process on all the local travel companies we work with, to check that they can serve you well.</p>
        <p>For more information, or for any questions you have don't hesitate to contact us.</p>
      </>
    ),
  },
  {
    title: "Can we split a payment across different travellers?",
    content: () => (
      <>
        <p>Yes, although this will need to be done by contacting the TravelLocal team directly.</p>
        <p>Please give us a call on {officePhone} - we're available {officeHours}.</p>
      </>
    ),
  },
  // Removed during COVID whilst we no longer offer flights, to be re-added once that's all over
  // {
  //   title: "What about flights?",
  //   content: () => (
  //     <>
  //       <p>We are perfectly placed to book any flights that you need to make your holiday happen. We provide flights from anywhere, to anywhere, and by booking them with your trip you benefit from complete ATOL protection. Simply <strong><a href="http://holidays.travellocal.com/flights">fill out our quick form with your request</a></strong> and our Flights Team will be in touch. For any advice, please email <strong><a href="mailto:flights@travellocal.com">flights@travellocal.com</a></strong>.</p>
  //     </>
  //   ),
  // },
];

interface IFaqAccordion extends FAQProps {
  heading: string;
}

export const FaqAccordion: React.FC<IFaqAccordion> = ({ heading, enquiry, itinerary }) => {
  return (
    <Accordion
      heading={heading}
      items={faqItems}
      renderData={{
        enquiry,
        itinerary,
      }}
    />
  );
};
